import { useWizardStore } from '@/store/wizardStore'
import type { BudgetBand } from '@/store/wizardStore'
import type { Lang } from '@/hooks/useLang'

interface Step8BudgetProps { lang: Lang }

const BANDS: {
  id: BudgetBand
  range: string
  icon: string
  accent: string
  fr: { title: string; desc: string; features: string[] }
  en: { title: string; desc: string; features: string[] }
}[] = [
  {
    id: 'companion',
    range: '12 000 – 24 000 €',
    icon: 'volunteer_activism',
    accent: 'text-primary',
    fr: {
      title: 'Companion',
      desc: 'Configuration d\'entrée, idéale pour les cas d\'usage légers et l\'assistance personnelle.',
      features: ['Mobilité de base', 'Interaction vocale standard', 'Capteurs essentiels', 'Support 8/5'],
    },
    en: {
      title: 'Companion',
      desc: 'Entry-level configuration, ideal for light use cases and personal assistance.',
      features: ['Basic mobility', 'Standard voice interaction', 'Essential sensors', '8/5 support'],
    },
  },
  {
    id: 'assistant_pro',
    range: '24 000 – 48 000 €',
    icon: 'work',
    accent: 'text-secondary',
    fr: {
      title: 'Assistant Pro',
      desc: 'Configuration professionnelle pour les déploiements sectoriels exigeants.',
      features: ['Navigation avancée', 'IA supervisée', 'Intégrations API', 'Support 24/5'],
    },
    en: {
      title: 'Assistant Pro',
      desc: 'Professional configuration for demanding sector deployments.',
      features: ['Advanced navigation', 'Supervised AI', 'API integrations', '24/5 support'],
    },
  },
  {
    id: 'operator_specialist',
    range: '48 000 – 96 000 €',
    icon: 'military_tech',
    accent: 'text-primary-container',
    fr: {
      title: 'Operator Specialist',
      desc: 'Configuration haut de gamme pour les missions critiques et l\'industrie lourde.',
      features: ['Pleine autonomie', 'IA adaptative', 'Redondance système', 'Support 24/7 dédié'],
    },
    en: {
      title: 'Operator Specialist',
      desc: 'High-end configuration for critical missions and heavy industry.',
      features: ['Full autonomy', 'Adaptive AI', 'System redundancy', 'Dedicated 24/7 support'],
    },
  },
]

export default function Step8Budget({ lang }: Step8BudgetProps) {
  const { draft, update } = useWizardStore()
  const selected = draft.step8_budget

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {BANDS.map((band, idx) => {
        const isSelected = selected === band.id
        const isMid = idx === 1
        return (
          <div
            key={band.id}
            onClick={() => update({ step8_budget: band.id })}
            className={`glass-card p-8 rounded-xl border border-outline-variant/20 flex flex-col gap-6 cursor-pointer relative ${isSelected ? 'ring-1 ring-primary-container bg-surface-container-highest/80' : ''} ${isMid ? 'md:-mt-4 md:mb-4' : ''}`}
          >
            {isMid && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-secondary text-on-secondary text-[9px] font-label tracking-widest uppercase px-3 py-1 rounded-full">
                {lang === 'fr' ? 'Recommandé' : 'Recommended'}
              </div>
            )}

            <div>
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 bg-surface-container-highest ${band.accent}`}>
                <span className="material-symbols-outlined text-2xl">{band.icon}</span>
              </div>
              <h3 className={`font-headline text-2xl font-bold mb-1 ${band.accent}`}>{band[lang].title}</h3>
              <div className="font-headline text-sm text-on-surface-variant font-light">{band.range}</div>
            </div>

            <p className="text-sm text-on-surface-variant leading-relaxed">{band[lang].desc}</p>

            <ul className="space-y-2 mt-auto">
              {band[lang].features.map((f) => (
                <li key={f} className="flex items-center gap-2 text-xs text-on-surface-variant font-label">
                  <span className="material-symbols-outlined text-sm text-primary/60">check</span>
                  {f}
                </li>
              ))}
            </ul>

            <div className={`text-xs font-bold tracking-widest uppercase flex items-center gap-1 transition-colors ${isSelected ? band.accent : 'text-on-surface-variant/40'}`}>
              {isSelected ? (lang === 'fr' ? 'Sélectionné' : 'Selected') : (lang === 'fr' ? 'Sélectionner' : 'Select')}
              <span className="material-symbols-outlined text-sm">
                {isSelected ? 'check_circle' : 'arrow_forward'}
              </span>
            </div>
          </div>
        )
      })}
    </div>
  )
}
